import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInstagram,
  faFacebook,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faBars, faTimes } from "@fortawesome/free-solid-svg-icons";

import logo from "../../assets/logo.png";
import { useHeaderScroll } from "../../hooks/useHeaderScroll";

export const DefaultLayout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const isHeaderVisible = useHeaderScroll({
    threshold: 30,
    enableOnMobile: true,
  });

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "home",
        "quemSomos",
        "tratamento",
        "instalacao",
        "depoimentos",
        "prevencao",
        "localizacao",
        "doacoes",
      ];
      const scrollPosition = window.scrollY + 100; // offset for header

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getNavClass = (section: string) => {
    return activeSection === section
      ? "text-gray-700 font-bold underline decoration-2 decoration-gray-700 underline-offset-4"
      : "text-brand-blue font-montserrat font-semibold hover:text-gray-700 hover:underline decoration-2 decoration-gray-700 underline-offset-4 transition-all duration-300";
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#home"
            onClick={() => scrollToSection("home")}
            className="flex items-center space-x-3"
          >
            <img src={logo} alt="PATRE Logo" className="h-12 w-auto" />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden xl-custom:flex space-x-8">
            <a
              href="#home"
              onClick={() => scrollToSection("home")}
              className={getNavClass("home")}
            >
              Home
            </a>
            <a
              href="#quemSomos"
              onClick={() => scrollToSection("quemSomos")}
              className={getNavClass("quemSomos")}
            >
              Quem Somos
            </a>
            <a
              href="#tratamento"
              onClick={() => scrollToSection("tratamento")}
              className={getNavClass("tratamento")}
            >
              Tratamento
            </a>
            <a
              href="#instalacao"
              onClick={() => scrollToSection("instalacao")}
              className={getNavClass("instalacao")}
            >
              Instalações
            </a>
            <a
              href="#depoimentos"
              onClick={() => scrollToSection("depoimentos")}
              className={getNavClass("depoimentos")}
            >
              Depoimentos
            </a>
            <a
              href="#prevencao"
              onClick={() => scrollToSection("prevencao")}
              className={getNavClass("prevencao")}
            >
              Prevenção
            </a>
            <a
              href="#localizacao"
              onClick={() => scrollToSection("localizacao")}
              className={getNavClass("localizacao")}
            >
              Localização
            </a>
            <a
              href="#doacoes"
              onClick={() => scrollToSection("doacoes")}
              className={getNavClass("doacoes")}
            >
              Doações
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="xl-custom:hidden text-brand-blue p-2 rounded-lg hover:bg-gray-100 transition-colors duration-300"
          >
            <FontAwesomeIcon
              icon={menuOpen ? faTimes : faBars}
              className="text-2xl"
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {menuOpen && (
          <div className="xl-custom:hidden bg-white border-t border-gray-200 shadow-lg">
            <nav className="max-w-7xl mx-auto px-4 py-6 space-y-4">
              <a
                href="#home"
                onClick={() => scrollToSection("home")}
                className={`block py-2 ${getNavClass("home")}`}
              >
                Home
              </a>
              <a
                href="#quemSomos"
                onClick={() => scrollToSection("quemSomos")}
                className={`block py-2 ${getNavClass("quemSomos")}`}
              >
                Quem Somos
              </a>
              <a
                href="#tratamento"
                onClick={() => scrollToSection("tratamento")}
                className={`block py-2 ${getNavClass("tratamento")}`}
              >
                Tratamento
              </a>
              <a
                href="#instalacao"
                onClick={() => scrollToSection("instalacao")}
                className={`block py-2 ${getNavClass("instalacao")}`}
              >
                Instalações
              </a>
              <a
                href="#depoimentos"
                onClick={() => scrollToSection("depoimentos")}
                className={`block py-2 ${getNavClass("depoimentos")}`}
              >
                Depoimentos
              </a>
              <a
                href="#prevencao"
                onClick={() => scrollToSection("prevencao")}
                className={`block py-2 ${getNavClass("prevencao")}`}
              >
                Prevenção
              </a>
              <a
                href="#localizacao"
                onClick={() => scrollToSection("localizacao")}
                className={`block py-2 ${getNavClass("localizacao")}`}
              >
                Localização
              </a>
              <a
                href="#doacoes"
                onClick={() => scrollToSection("doacoes")}
                className={`block py-2 ${getNavClass("doacoes")}`}
              >
                Doações
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Logo and Brand */}
            <div className="flex flex-col items-center md:items-start">
              <img src={logo} alt="PATRE Logo" className="h-16 w-auto mb-4" />
              <p className="text-gray-300 font-roboto text-center md:text-left">
                Comunidade Terapêutica Cristo Rei
              </p>
            </div>

            {/* Contact Info */}
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold font-montserrat mb-4 text-brand-yellow">
                Contato
              </h3>
              <p className="text-gray-300 font-roboto mb-2">
                Linha Almeida, Capela Nossa Senhora da Saúde, 502 - Interior
              </p>
              <p className="text-gray-300 font-roboto mb-2">
                Antônio Prado - RS - Brasil
              </p>
              <p className="text-gray-300 font-roboto">
                54 99699.8885 - 54 99930.2788
              </p>
            </div>

            {/* Social Media */}
            <div className="text-center md:text-right">
              <h3 className="text-xl font-bold font-montserrat mb-4 text-brand-yellow">
                Redes Sociais
              </h3>
              <div className="flex justify-center md:justify-end space-x-4">
                <a
                  href="https://wa.me/5554996998885?text=Ol%C3%A1%2C%20vim%20pelo%20site%20da%20Comunidade%20Terap%C3%AAutica%20Cristo%20Rei%20PATRE%20e%20gostaria%20de%20saber%20mais%20sobre%20o%20tratamento."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-400 hover:text-green-300 transition-colors duration-300 text-2xl"
                >
                  <FontAwesomeIcon icon={faWhatsapp} />
                </a>
                <a
                  href="https://instagram.com/ctpatre?igshid=MzRlODBiNWFlZA=="
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-400 hover:text-pink-300 transition-colors duration-300 text-2xl"
                >
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
                <a
                  href="https://www.facebook.com/patre.antonioprado?mibextid=ZbWKwL"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 transition-colors duration-300 text-2xl"
                >
                  <FontAwesomeIcon icon={faFacebook} />
                </a>
                <a
                  href="mailto:ctpatre@gmail.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-gray-300 transition-colors duration-300 text-2xl"
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-center">
            <p className="text-gray-400 font-roboto">
              © 2025 Comunidade Terapêutica Cristo Rei PATRE. Todos os direitos
              reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
